import { FormControlLabel, Checkbox } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectBookingApprovedCheckboxSpecs } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';
import { CustomTooltip } from '@AMIEWEB/Common';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { placementStatusOptions } from '@AMIEWEB/Placement/NextPlacementStatus/StatusDefaults';
import { convertToFormat } from 'app/helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import { missingField } from 'app/constants';
import { ff_isPlacementStatusDisabled } from '@AMIEWEB/Placement/NextPlacementStatus/Helper';
import { useDecision } from '@optimizely/react-sdk';
import { ff_placementStatusChangeKey } from 'app/constants/FeatureFlags/Placement/Keys';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { useReadOnly } from 'oidc/userRoles';

const useStyles = makeStyles()(theme => ({
  bookingControl: {
    margin: 0,
    color: theme.palette.system.darkGray,
    lineHeight: 1.85,
  },
  label: {
    fontSize: '12px !important',
    '&.Mui-disabled': {
      color: `${theme.palette.system.darkGray} !important`,
    },
  },
  denseCheckbox: {
    padding: '0 12px 0 0',
    marginTop: -2,
  },
  whiteSpace: {
    whiteSpace: 'pre',
  },
  checked: {
    opacity: 0.6,
  },
}));

export const BookingApprovedCheckbox = () => {
  const { cx, classes } = useStyles();
  const { t } = useTranslation();
  const { isCheckBoxDisabled, isCheckBoxSelected, disabledReasons, approvedBy, approvedByName, approvalDate } =
    useSelector(selectBookingApprovedCheckboxSpecs);

  const [statusChangeDecision] = useDecision(ff_placementStatusChangeKey, { autoUpdate: true });
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const params = useParams<{ placementId: string }>();
  const { readOnly} = useReadOnly();

  const dispatch = useDispatch();

  // Derive checkbox state from current placement status
  const isCurrentlyBookingApproved = React.useMemo(() => {
    return currentPlacementStatus?.activityTypeId === placementStatusOptions.bookingApproved.activityTypeId &&
           currentPlacementStatus?.activityStatusId === placementStatusOptions.bookingApproved.activityStatusId;
  }, [currentPlacementStatus]);

  // Use current status to determine if checkbox should be checked and disabled
  const effectiveIsChecked = isCurrentlyBookingApproved || isCheckBoxSelected;
  const effectiveIsDisabled = isCheckBoxDisabled || isCurrentlyBookingApproved;

  // Refresh checkbox specs when placement status changes
  React.useEffect(() => {
    if (currentPlacementStatus && params.placementId) {
      const placementId = parseInt(params.placementId);
      if (placementId > 0) {
        dispatch(placementModsAction.getBookingApprovedCheckboxSpecs({ placementId }));
      }
    }
  }, [currentPlacementStatus, params.placementId, dispatch]);

  const tooltipContent = React.useMemo(() => {
    if (!currentPlacementStatus || !statusChangeDecision) return null;
    const isFeatureDisabled = ff_isPlacementStatusDisabled(
      statusChangeDecision,
      placementStatusOptions.bookingApproved,
      currentPlacementStatus,
    );

    if (!isCheckBoxDisabled) return null;
    const reasons = !!approvedBy
      ? [
          `\u25CF\u00A0${t('placement.placementMods.pendingMods.approvalCheckbox.approvedBy', {
            approvedBy: approvedByName,
          })}`,
          t('placement.placementMods.pendingMods.approvalCheckbox.approvalDate', {
            approvalDate: !approvalDate ? missingField : convertToFormat(approvalDate, 'MM/DD/YYYY hh:mm a', true),
          }),
        ]
      : [...Object.values(disabledReasons)].length > 0
      ? [t('placement.placementStatusPickList.disabledDueTo'), ...Object.values(disabledReasons)]
      : isFeatureDisabled
      ? [
          t('placement.placementStatusPickList.disabledDueTo'),
          t('placement.placementStatusPickList.transitionIsDisabled'),
        ]
      : null;
    return reasons ? reasons.join('\n\u25CF\u00A0') : null;
  }, [
    currentPlacementStatus,
    statusChangeDecision,
    t,
    isCheckBoxDisabled,
    approvedBy,
    approvedByName,
    approvalDate,
    disabledReasons,
  ]);

  const handleChange = React.useCallback(newState => {
    // Prevent action if already booking approved
    if (isCurrentlyBookingApproved) {
      return;
    }
    
    dispatch(placementStatusAction.setSelectedStatus({ status: placementStatusOptions.bookingApproved }));
    
    // Refresh the modifications list when checkbox is checked
    const placementId = parseInt(params.placementId);
    if (placementId > 0) {
      dispatch(placementModsAction.getPlacementModsList({ placementId }));
      // Also refresh the checkbox specs to ensure proper state
      dispatch(placementModsAction.getBookingApprovedCheckboxSpecs({ placementId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.placementId, isCurrentlyBookingApproved]);

  const checkbox = React.useMemo(
    () => (
      <FormControlLabel
        classes={{
          root: classes.bookingControl,
          label: classes.label,
        }}
        onChange={(e, newState) => handleChange(newState)}
        disabled={effectiveIsDisabled || readOnly}
        checked={effectiveIsChecked}
        control={
          <Checkbox
            size="small"
            classes={{ root: classes.denseCheckbox, disabled: cx({ [classes.checked]: effectiveIsChecked }) }}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            color="primary"
          />
        }
        label={t('placement.placementMods.pendingMods.approvalCheckbox.label')}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleChange, effectiveIsChecked, effectiveIsDisabled],
  );

  return effectiveIsDisabled ? (
    <CustomTooltip disabled={!tooltipContent} classes={{ tooltip: classes.whiteSpace }} tooltipContent={tooltipContent}>
      {checkbox}
    </CustomTooltip>
  ) : (
    checkbox
  );
};
